import { useMemo } from "react"
import { useFragment, useLazyLoadQuery } from "react-relay"
import { graphql } from "relay-runtime"
import { allowsFollowUp } from "@/lib/appointment-type-rules"
import { getServiceMapping } from "@/lib/fetch-service-mappings"
import { getServerDateOnly } from "@/lib/initial-time-zone"
import { useDefaultValuesQuery } from "@/modules/patients/agenda/teleconsultation/booking/hooks/__generated__/useDefaultValuesQuery.graphql"
import {
  isChatSelectable,
  resolveAddressSource,
} from "@/modules/patients/agenda/teleconsultation/booking/libs/teleconsultation-helper"
import { TeleconsultationMode } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-drawer"
import {
  PartnerUser,
  TeleconsultationForm,
} from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { PatientAddress } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { PatientData } from "@/modules/patients/detail/store/usePatientStore"
import { getLineTypeServiceDefault } from "@/modules/patients/helpers/patient-helper"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import useCallContextStore from "@/stores/useCallContextStore"
import {
  CallerRole,
  CommunicationChannel,
  LineType,
  MedgateConnectChannel,
  Service,
  ServiceSpecialty,
  Urgency,
} from "@/types/__generated__/union-types"

const inboundCallLineQuery = graphql`
  query useDefaultValuesQuery($extensionId: String, $skip: Boolean!) {
    ch_telephoneLines(extensionId: $extensionId) @skip(if: $skip) {
      id
      key
      lineType
    }
  }
`

export type InboundCallContext = {
  callerPhoneNumber?: string | null
  callerRole?: CallerRole | null
  inboundCallId?: number | null
  telephoneLineKey?: string | null
  telephoneLineType?: LineType
  callId?: string | null
}

type TeleconsultationDefaults = {
  mode: TeleconsultationMode
  callerPhoneNumber: string
  defaultValues: Partial<TeleconsultationForm>
}

const getHasMedgateTeleGeneralPractitioner = (countryPatient: NonNullable<PatientData>) => {
  return countryPatient.insurees.some((x) =>
    x.coverages.some((c) => c.product.productConfiguration?.isMedgateTeleGeneralPractitioner),
  )
}

const findPartner = (partners: PartnerUser[], email?: string | null) =>
  email ? partners.find((p) => p.email === email) : undefined

const useDefaultValues = (
  incomingMode: TeleconsultationMode,
  countryPatient: NonNullable<PatientData>,
  partners: PartnerUser[],
): TeleconsultationDefaults => {
  const patient = useFragment<PatientFragment$key>(PatientFragment, countryPatient)
  const { primaryLanguage, phoneNumber: patientPhoneNumber, country: patientCountry, age: patientAge } = patient

  const hasMedgateTeleGeneralPractitioner = getHasMedgateTeleGeneralPractitioner(countryPatient)
  const { contactChannel } = countryPatient
  const { isMedicalChatEnabled } = countryPatient.medgateServiceInfo

  const callerPhoneNumber = useCallContextStore((state) => state.callerPhoneNumber)
  const callLineCode = useCallContextStore((state) => state.callLineCode)
  const callId = useCallContextStore((state) => state.callID)
  const callerRole = useCallContextStore((state) => state.callerRole)
  const inboundCallId = useCallContextStore((state) => state.inboundCallId)

  const isInboundCallContextApplicable = incomingMode.kind !== "EDITING_APPOINTMENT"
  const shouldLoadCallLine = isInboundCallContextApplicable && !!callLineCode

  const callLineData = useLazyLoadQuery<useDefaultValuesQuery>(inboundCallLineQuery, {
    extensionId: callLineCode,
    skip: !shouldLoadCallLine,
  })

  const mode = useMemo<TeleconsultationMode>(() => {
    const telephoneLine = callLineData.ch_telephoneLines?.[0]

    const inboundCallContext: InboundCallContext = {
      callerPhoneNumber,
      callerRole,
      inboundCallId,
      telephoneLineKey: telephoneLine?.key,
      telephoneLineType: telephoneLine?.lineType,
      callId,
    }

    switch (incomingMode.kind) {
      case "ADD_APPOINTMENT":
        return { kind: incomingMode.kind, data: { ...incomingMode.data, ...inboundCallContext } }
      case "FOLLOWUP_FOR_PROGRESS_NOTE":
        return { kind: incomingMode.kind, data: { ...incomingMode.data, ...inboundCallContext } }
      case "EDITING_APPOINTMENT":
        return incomingMode
    }
  }, [incomingMode, callerPhoneNumber, callerRole, inboundCallId, callId, callLineData.ch_telephoneLines])

  const defaultValues = useMemo<Partial<TeleconsultationForm>>(() => {
    const serverDate = getServerDateOnly()
    const defaults: TeleconsultationForm = {
      id: null,
      date: serverDate,
      slotId: null,
      urgency: "NORMAL",
      flexibility: "FLEXIBLE",
      appointmentType: "NORMAL",
      followUpForProgressNoteId: "",
      channel: hasMedgateTeleGeneralPractitioner ? "VIDEO" : "PHONE",
      patientPhoneNumber: "",
      service: hasMedgateTeleGeneralPractitioner ? "TELE_GENERAL_PRACTITIONER" : "CONSULTATION",
      specialty: "NONE",
      requestType: "NONE",
      language: primaryLanguage,
      reason: "",
      agentUserName: null,
      gender: null,
      addressSource: undefined,
      postalCode: null,
      city: null,
      country: patientCountry,
      houseNumberStreet: null,
      preventionProgramKeys: [],
      assignedPartner: null,
      partnerType: null,
      isPartnerDoc2U: null,
    }

    switch (mode.kind) {
      case "ADD_APPOINTMENT": {
        const service =
          mode.data.defaultService ??
          getLineTypeServiceDefault(mode.data.telephoneLineType, patientAge.years) ??
          defaults.service

        return {
          ...defaults,
          patientPhoneNumber: getDefaultPhoneNumber(
            mode,
            undefined,
            mode.data.callerPhoneNumber ?? "",
            patientPhoneNumber,
          ),
          reason: mode.data.defaultReason ?? "",
          service,
          preventionProgramKeys: mode.data.defaultPreventionProgramKey ? [mode.data.defaultPreventionProgramKey] : [],
        }
      }
      case "EDITING_APPOINTMENT": {
        const { appointment } = mode.data
        const {
          assignedTo,
          patientAddress,
          desiredAgentGender,
          date,
          slotId,
          medgateConnectChannel,
          partnerUserEmail,
        } = appointment
        const appointmentDate = getServerDateOnly(new Date(date))
        const partner = findPartner(partners, partnerUserEmail)

        return {
          ...defaults,
          ...appointment,

          // Schedule
          date: serverDate.isGreaterOrEqualThan(appointmentDate) ? serverDate : appointmentDate,
          slotId: serverDate.isGreaterThan(appointmentDate) ? null : slotId,

          // Agent
          agentUserName: assignedTo,
          gender: desiredAgentGender ?? null,
          followUpForProgressNoteId: appointment.followUpForProgressNote?.id ?? undefined,
          patientPhoneNumber: getDefaultPhoneNumber(mode, partner?.phoneNumber, "", patientPhoneNumber),
          preventionProgramKeys: [...appointment.preventionProgramKeys],

          // Address
          ...resolveAddress(patientAddress, medgateConnectChannel, appointment.patientPhoneNumber),

          // Medgate Connect
          assignedPartner: partnerUserEmail,
          partnerType: partner?.userTag ?? null,
          isPartnerDoc2U: partner?.isDoc2U ?? null,
        }
      }
      case "FOLLOWUP_FOR_PROGRESS_NOTE": {
        const { progressNote } = mode.data
        const { appointment: precedingAppointment, createdBy } = progressNote
        const {
          language,
          channel,
          reason,
          service,
          specialty,
          patientAddress,
          partnerUserEmail,
          medgateConnectChannel,
        } = precedingAppointment

        const isEmergencyService = getServiceMapping().serviceMappings.find(
          (mapping) => mapping.service === service,
        )?.isUrgent
        const partner = findPartner(partners, partnerUserEmail)
        const followUpService = getDefaultServiceFollowup(service)
        const isChatSelectableForFollowUp = isChatSelectable({
          contactChannel,
          service: followUpService,
          isMedicalChatEnabled,
        })

        return {
          ...defaults,
          patientPhoneNumber: getDefaultPhoneNumber(
            mode,
            partner?.phoneNumber,
            mode.data.callerPhoneNumber ?? "",
            patientPhoneNumber,
          ),

          // Agent & follow-up
          agentUserName: createdBy,
          appointmentType: "FOLLOW_UP",
          followUpForProgressNoteId: progressNote.id,

          // Service
          channel: getDefaultChannelFollowup(channel, isChatSelectableForFollowUp),
          language,
          reason,
          specialty: getDefaultSpecialtyFollowup(service, specialty),
          service: followUpService,
          urgency: getDefaultUrgencyFollowup(service, isEmergencyService ? "MEDICAL_URGENT" : defaults.urgency),

          // Address
          ...resolveAddress(patientAddress, medgateConnectChannel, precedingAppointment.patientPhoneNumber),

          // Medgate Connect
          assignedPartner: partnerUserEmail ?? null,
          partnerType: partner?.userTag ?? null,
          isPartnerDoc2U: partner?.isDoc2U ?? null,
        }
      }
    }
  }, [
    mode,
    partners,
    patientAge,
    patientPhoneNumber,
    primaryLanguage,
    patientCountry,
    hasMedgateTeleGeneralPractitioner,
    contactChannel,
    isMedicalChatEnabled,
  ])

  return {
    mode,
    callerPhoneNumber: isInboundCallContextApplicable ? callerPhoneNumber : "",
    defaultValues,
  }
}

const resolveAddress = (
  patientAddress: PatientAddress,
  medgateConnectChannel?: MedgateConnectChannel | null,
  patientPhoneNumber?: string | null,
) => ({
  addressSource: resolveAddressSource(medgateConnectChannel, patientPhoneNumber, !!patientAddress),
  postalCode: patientAddress?.postalCode ?? null,
  city: patientAddress?.city ?? null,
  country: patientAddress?.countryCode ?? null,
  houseNumberStreet: patientAddress?.houseNumberStreet ?? null,
})

const getDefaultServiceFollowup = (service: Service): Service => {
  if (service === "KIDS_LINE" || service === "EMERGENCY_LINE" || !allowsFollowUp(service)) {
    return "CONSULTATION"
  }
  return service
}

const getDefaultChannelFollowup = (
  channel: CommunicationChannel,
  isChatSelectableForFollowUp: boolean,
): CommunicationChannel => {
  if (channel === "ASYNC_REQUEST" || (channel === "CHAT" && !isChatSelectableForFollowUp)) {
    return "PHONE"
  }
  return channel
}

const getDefaultUrgencyFollowup = (service: Service, urgency: Urgency): Urgency => {
  if (service === "KIDS_LINE" || service === "EMERGENCY_LINE") {
    return "NORMAL"
  }
  return urgency
}

const getDefaultSpecialtyFollowup = (service: Service, specialty: ServiceSpecialty): ServiceSpecialty => {
  switch (service) {
    case "KIDS_LINE":
      return "PEDIATRICS"
    case "EMERGENCY_LINE":
    case "MEDGATE_CONNECT":
    case "PSYCHOSOCIAL":
      return "NONE"
    case "ADMINISTRATIVE_REQUEST":
    case "CONSULTATION":
    case "PREVENTION_ASSESSMENT":
    case "STUDY_CALL":
    case "TELE_GENERAL_PRACTITIONER":
      return specialty
  }
}

const getDefaultPhoneNumber = (
  mode: TeleconsultationMode,
  partnerPhoneNumber: string | null | undefined,
  callerPhoneNumber: string,
  patientPhoneNumber: string | null,
): string | null => {
  switch (mode.kind) {
    case "ADD_APPOINTMENT":
      return callerPhoneNumber || patientPhoneNumber
    case "EDITING_APPOINTMENT":
      // Do not prefill the caller (IBC) phone number when editing an existing appointment:
      // the stored value is the source of truth. A null stored value means "use the patient's
      // correspondence phone", so it must fall back to patientPhoneNumber, not the active caller number.
      return mode.data.appointment.patientPhoneNumber || partnerPhoneNumber || patientPhoneNumber
    case "FOLLOWUP_FOR_PROGRESS_NOTE":
      return (
        mode.data.progressNote.appointment.patientPhoneNumber ||
        partnerPhoneNumber ||
        callerPhoneNumber ||
        patientPhoneNumber
      )
  }
}

export {
  getDefaultPhoneNumber,
  getDefaultServiceFollowup,
  getDefaultSpecialtyFollowup,
  getDefaultUrgencyFollowup,
  resolveAddress,
  useDefaultValues,
}
